import { useMemo } from "react";
import { trpc, type RouterOutputs } from "../trpc";

type ControlledAppAccess = RouterOutputs["account"]["controlledAppAccess"];

/** Builds `isAppRestricted(appId)` from the controlled-apps response (ids are versionless). */
export function buildIsAppRestricted(data: ControlledAppAccess | undefined) {
  const strategy = data?.strategy ?? null;
  const apps = data?.apps ?? [];
  const deniedApis = new Set(
    apps.filter((app) => app.status === "denied").map((app) => app.selectedApi),
  );
  const allowedApis = new Set(
    apps
      .filter((app) => app.status === "allowed")
      .map((app) => app.selectedApi),
  );

  return (appId: string): boolean => {
    const strippedId = appId?.split("@")[0] ?? "";
    if (!strippedId || !strategy) return false;

    if (strategy === "denylist") {
      return deniedApis.has(strippedId);
    }

    return !allowedApis.has(strippedId);
  };
}

// This hook models the allowlist/denylist strategy being migrated to a new
// open/standard/managed/blocked control-level model:
// https://gitlab.com/zapier/engineering-index/-/merge_requests/2136
// Revisit isAppRestricted's decision logic once that ships.
export function useControlledAppAccess({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { data, isLoading, isError } =
    trpc.account.controlledAppAccess.useQuery(undefined, {
      enabled,
      // Controlled-app restrictions for the user change rarely; avoid refetching (up
      // to 25 paginated monolith requests) on every mount/window focus.
      staleTime: 1000 * 60 * 5,
    });

  const isAppRestricted = useMemo(() => buildIsAppRestricted(data), [data]);

  return {
    isAppRestricted,
    strategy: data?.strategy ?? null,
    isLoading,
    isError,
  };
}
